/**
 * Domain Runtime Contracts (`domain-def@4`)
 *
 * The compiled artifact from `compileDomain()`. `DomainRuntime` is the unified
 * *superset* runtime — it carries BOTH:
 *
 * - the **rich** fields of the framework lineage (`routes`, `authzRules`,
 *   `umaResources`, `openapi`, `get(urn)`, `checkAuthz`, `createClient`,
 *   `validate`), and
 * - the **generically-typed** fields of the career/dna lineage (`types` as a
 *   real `Module<scope.infer<…>>` carrying inferred shapes, and per-resource
 *   `ResourceAccessor`s).
 *
 * It is generic over the domain definition `D` and the app-injected primitive
 * set `P`, with defaults (`DomainDefinitionJSON`, `{}`) so non-generic callers
 * keep working. The two type parameters are what let `runtime.types.X.infer`
 * resolve to the real (discriminated-union-intact) shape end-to-end.
 */

import type { Module, scope } from "arktype";
import type { DomainDefinitionJSON } from "../schema/domain-definition";
import type { OpenAPISpec } from "../compiler/compile-openapi";
import type { DomainClient, ClientConfig } from "../compiler/generate-client";

// ---------------------------------------------------------------------------
// Core primitives + scope merge (the type-inference spine)
// ---------------------------------------------------------------------------

/**
 * Platform core primitives — merged first into every compiled ArkType scope,
 * before the app's injected primitives and the domain's own types. Kept
 * minimal and universal; app-specific spines (career, dna, …) are injected via
 * `compileDomain(def, { primitives })`.
 */
export const coreDomain = {
  /** UUID identifier. */
  EntityId: "string.uuid",
  /** Non-negative currency amount. */
  Money: "number >= 0",
  /** Positive integer quantity. */
  Quantity: "number.integer > 0",
} as const;

/**
 * The type-level scope fed to ArkType's `scope()` — core primitives, then the
 * app-injected primitives `P`, then this definition's own `types`. `scope.infer`
 * over this drives the typed `Module` in {@link DomainRuntime.types}.
 *
 * MANDATORY for inference: the compiler's `types` field is explicitly annotated
 * `Module<scope.infer<MergedScope<D, P>>>` — TS will NOT infer it through the
 * scope-input spread on its own.
 */
export type MergedScope<D extends DomainDefinitionJSON, P> =
  typeof coreDomain & P & D["types"];

// ---------------------------------------------------------------------------
// Validation + resource accessors (career lineage)
// ---------------------------------------------------------------------------

/** Validation result — either the validated data or an error summary. */
export type ValidationResult<T = unknown> =
  | { success: true; data: T }
  | { success: false; errors: string };

/** Per-resource accessor returned by the compiled runtime. */
export interface ResourceAccessor {
  /** Fetch and validate resource data from the resolved URL. */
  fetch(params: Record<string, string>): Promise<unknown>;
  /** Validate data against the resource's root type. */
  validate(data: unknown): ValidationResult;
  /** The resource's root type name. */
  typeName: string;
}

// ---------------------------------------------------------------------------
// Rich runtime types (framework lineage)
// ---------------------------------------------------------------------------

/** URN parse result containing extracted parameters. */
export interface UrnParams {
  [key: string]: string;
}

/** Compiled authorization rule for a single resource. */
export interface CompiledAuthzRule {
  resourceKey: string;
  visibility: "public" | "private" | "consent" | "user-owned";
  requiredScopes: string[];
  requiredRoles: string[];
  ownerAttribute?: string;
  ownerManagedAccess: boolean;
}

/** Authorization check result. */
export type AuthzResult =
  | { allowed: true }
  | { allowed: false; reason: string; umaTicket?: string };

/** Route handler context. */
export interface RouteContext {
  params: UrnParams;
  /** Authenticated user info (null if unauthenticated). */
  user: { id: string; roles: string[]; attributes: Record<string, string> } | null;
}

/** Compiled route definition. */
export interface CompiledRoute {
  /** HTTP method. */
  method: string;
  /** URL pattern, e.g. "/api/bible/:organizationId/summary". */
  urlPattern: string;
  /** Resource key in the domain definition. */
  resourceKey: string;
  /** Type name for response validation. */
  typeName: string;
  /** Compiled authorization rule. */
  authz: CompiledAuthzRule;
}

/** UMA resource definition for Keycloak registration. */
export interface UmaResourceDefinition {
  /** Resource name. */
  name: string;
  /** Display name. */
  displayName: string;
  /** URN pattern. */
  uri: string;
  /** Resource type. */
  type: string;
  /** Available scopes. */
  scopes: string[];
  /** Owner-managed access flag. */
  ownerManagedAccess: boolean;
  /** Additional attributes. */
  attributes: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// Domain Runtime Interface (the superset)
// ---------------------------------------------------------------------------

/**
 * Compiled domain runtime — the executable artifact from `compileDomain()`.
 *
 * Generic over the domain definition `D` and the injected primitive set `P`.
 * Both default so non-generic callers (`DomainRuntime`) still type-check; a
 * caller that authors its definition `as const satisfies DomainDefinitionJSON`
 * and lets inference flow gets `types.X.infer` carrying real shapes.
 */
export interface DomainRuntime<
  D extends DomainDefinitionJSON = DomainDefinitionJSON,
  P = {},
> {
  /** Original JSON definition (for introspection). */
  readonly def: D;
  /** Domain name. */
  readonly name: string;
  /** Domain version. */
  readonly version: string;

  /**
   * Compiled ArkType module with all domain types.
   *
   * Statically typed via `scope.infer<MergedScope<D, P>>`: each member carries
   * its inferred shape (e.g. `types.EvidenceClaim.infer` is the real
   * `EvidenceClaim`, discriminated unions intact), so consumers derive TS types
   * as `typeof types.X.infer` instead of hand-mirroring shapes. Requires the
   * definition to be authored `as const satisfies DomainDefinitionJSON`.
   */
  readonly types: Module<scope.infer<MergedScope<D, P>>>;

  /** Per-resource accessors (`fetch` / `validate` / `typeName`). */
  readonly resources: Record<string, ResourceAccessor>;

  /** Compiled routes with authz. */
  readonly routes: CompiledRoute[];
  /** Compiled authorization rules indexed by resource key. */
  readonly authzRules: Record<string, CompiledAuthzRule>;
  /** UMA resource definitions for Keycloak registration. */
  readonly umaResources: UmaResourceDefinition[];
  /** Generated OpenAPI 3.1 specification. */
  readonly openapi: OpenAPISpec;

  /**
   * Fetch and validate a domain entity by URN.
   * @param urn - URN identifying the entity.
   * @returns Validated entity data.
   */
  get(urn: string): Promise<unknown>;

  /**
   * Check authorization for a resource access.
   * @param resourceKey - Resource key from the definition.
   * @param context - Route context with user info.
   */
  checkAuthz(resourceKey: string, context: RouteContext): AuthzResult;

  /**
   * Validate data against a named type.
   *
   * `@4` returns a {@link ValidationResult} (non-throwing) — reconciling the
   * `@2` throwing `validate` with the career lineage's result-typed one, which
   * every downstream consumer now reads.
   *
   * @param typeName - Type name from the definition.
   * @param data - Data to validate.
   */
  validate(typeName: string, data: unknown): ValidationResult;

  /**
   * Create a typed fetch-based client for this domain.
   * @param config - Client configuration (baseUrl, auth).
   */
  createClient(config: ClientConfig): DomainClient;
}
